import { useState, type FormEvent } from 'react';
import type { TopicComment } from '@/mocks/topics';

interface CommentSectionProps {
  comments: TopicComment[];
}

const stanceStyle = (stance: string) => {
  if (stance === '正方') return 'bg-primary-100 text-primary-700';
  if (stance === '反方') return 'bg-accent-100 text-accent-800';
  return 'bg-secondary-100 text-secondary-800';
};

export default function CommentSection({ comments }: CommentSectionProps) {
  const [list, setList] = useState<TopicComment[]>(comments);
  const [content, setContent] = useState('');
  const [stance, setStance] = useState('中立');
  const [liked, setLiked] = useState<string[]>([]);
  const [tip, setTip] = useState('');

  const toggleLike = (id: string) => {
    const isLiked = liked.includes(id);
    setLiked((prev) => (isLiked ? prev.filter((x) => x !== id) : [...prev, id]));
    setList((prev) =>
      prev.map((c) => (c.id === id ? { ...c, likes: c.likes + (isLiked ? -1 : 1) } : c)),
    );
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = content.trim();
    if (!text) {
      setTip('说点什么再发布吧。');
      return;
    }
    const newComment: TopicComment = {
      id: `local-${Date.now()}`,
      user: '我',
      stance,
      content: text.slice(0, 500),
      time: '刚刚',
      likes: 0,
    };
    setList((prev) => [newComment, ...prev]);
    setContent('');
    setStance('中立');
    setTip('已发布到讨论区，感谢你的观点。');
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-600">我的立场</span>
          {['正方', '反方', '中立'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStance(s)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1 text-xs transition-colors ${
                stance === s ? stanceStyle(s) : 'border border-background-300 text-foreground-600 hover:border-primary-300'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        <textarea
          value={content}
          onChange={(e) => {
            setContent(e.target.value.slice(0, 500));
            if (tip) setTip('');
          }}
          maxLength={500}
          rows={3}
          placeholder="说说你的看法，理性讨论更容易被点赞。"
          className="mt-3 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />

        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="text-xs text-foreground-400">{content.length}/500</span>
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            发布评论
          </button>
        </div>

        {tip && <p className="mt-3 text-sm text-primary-700">{tip}</p>}
      </form>

      <ul className="mt-6 space-y-4">
        {list.map((c) => (
          <li key={c.id} className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200 text-sm font-medium text-foreground-700">
                  {c.user.slice(0, 1)}
                </span>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground-900">{c.user}</span>
                  <span className="text-xs text-foreground-400">{c.time}</span>
                </div>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs ${stanceStyle(c.stance)}`}>{c.stance}</span>
            </div>

            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{c.content}</p>

            <div className="mt-3 flex items-center gap-4 text-xs text-foreground-500">
              <button
                type="button"
                onClick={() => toggleLike(c.id)}
                className={`flex cursor-pointer items-center gap-1 transition-colors hover:text-primary-600 ${
                  liked.includes(c.id) ? 'text-primary-600' : ''
                }`}
              >
                <i className={liked.includes(c.id) ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} />
                {c.likes}
              </button>
              <span className="flex items-center gap-1">
                <i className="ri-chat-1-line" />
                回复
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}